import { Card, CardContent, CardHeader, CardTitle, Input, ScrollArea } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PredicateInput } from '@/features/PredicateInput';
import { SaveButton } from '@/shared/ui';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { EditableGroupTitle } from './EditableGroupTitle';
import { UserGroupFromValues } from './types';
import { UsersList } from '../UsersList';

type Props = {
  isCreating: boolean;
  onSave: (group: UserGroupFromValues) => void;
  onDelete: () => void;
  onUpdateName?: (name: string) => void;
  isLoadingSaving: boolean;
};

export const UsersGroupsForm: FC<Props> = ({ isCreating, isLoadingSaving, onSave, onDelete, onUpdateName }) => {
  const { t } = useTranslation(['common', 'users']);

  const { handleSubmit, control } = useFormContext<UserGroupFromValues>();
  const { isDirty } = useFormState({ control });

  const onSubmit = handleSubmit(onSave);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center gap-5 border-b">
        {isCreating ? (
          <CardTitle>{t('users:newGroup')}</CardTitle>
        ) : (
          <EditableGroupTitle isCreate={isCreating} onUpdateName={onUpdateName} />
        )}
        <SaveButton
          className="ml-auto"
          isDirty={isDirty}
          isLoading={isLoadingSaving}
          onClick={onSubmit}
          secondaryAction={
            !isCreating && (
              <ConfirmationButton
                label={t('users:deleteGroup')}
                onConfirm={onDelete}
                title={t('users:deleteGroupTitle')}
                description={t('users:deleteGroupDescription')}
              />
            )
          }
        />
      </CardHeader>
      <CardContent className="overflow-hidden flex flex-col gap-6 p-0">
        <ScrollArea className="h-full">
          <div className="flex flex-col gap-6 p-6">
            {isCreating && (
              <Input
                control={control}
                name="name"
                required
                label={t('users:name')}
                placeholder={t('users:newGroup')}
                rules={{
                  required: {
                    value: true,
                    message: t('common:nameIsTheFirst'),
                  },
                }}
                className="max-w-md"
              />
            )}
            <PredicateInput label={t('users:description')} name="predicate" required control={control} />

            <UsersList />
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
